/**
 * <TutorManageSessionFlow> — « Gérer une séance » : le masteur signale un retard, une absence, ou
 * déplace un individuel (template).
 *
 * Le legacy : `tutor/lives/ManageRDVScreen.js` (route `move-session`, ouverte en touchant une
 * séance sans demande en cours — l'ancien bouton « Gérer les RDV » de `TutorLiveCardComponent`),
 * son `FormStepper` et ses étapes (`SelectSituationComponent`, `SelectDelayComponent`,
 * `MoveSessionReasonComponent`, `AbsenceImpactComponent`). Ses mots, ses cas, ses gardes ; la forme
 * de tous les gestes du design system : une feuille d'étapes (`<ParentFlowModal>`, la coquille des
 * gestes — son nom est celui du premier espace qui l'a eue), UNE question par étape, deux actions
 * au plus, la séance rappelée en tête.
 *
 * Les parcours :
 *  - **un cours partagé** — « Décris ta situation » :
 *     - en retard (le jour même seulement) → combien (5, 10, 15 min) → la confirmation ;
 *     - absent (pas un essai) → « Confirme ton absence » (l'impact élève par élève, les crédits,
 *       la case à cocher) → le rattrapage à proposer, quand il y en a un → le message aux familles
 *       et les consignes du remplaçant ;
 *     - plus disponible ce créneau, toutes les semaines (sauf remplacement accepté) → l'écran
 *       sort vers « Libérer des créneaux », ou propose le support ;
 *     - autre → le support ;
 *  - **un individuel** — « Que souhaites-tu faire ? » : déplacer cette séance, ou toutes les
 *    séances du créneau → la nouvelle date (ou le nouveau créneau) → la raison, le message aux
 *    parents et l'accord de l'élève → c'est fait (« les parents seront notifiés par SMS »).
 *
 * Le gabarit ne fait qu'enchaîner : chaque étape est un organisme (`<TutorSituationStep>`,
 * `<TutorDelayMinutesStep>`, `<TutorDelayConfirmStep>`, `<TutorAbsenceConfirmStep>`,
 * `<TutorMakeupStep>`, `<TutorAbsenceMessageStep>`, `<TutorNewDateStep>`,
 * `<TutorRescheduleReasonStep>`), les boîtes sont `<TutorManageSessionDialog>`. Ici vivent la
 * question de chaque étape, son pied (l'action et « Annuler ») et la barre de progression.
 *
 * Chaque étape reçoit son objet de props (`situation`, `delay`, `absence`, `makeup`, `message`,
 * `newDate`, `reschedule`) : celles de l'organisme qu'elle pose, moins ce que le parcours lui donne
 * déjà (la séance, le créneau, le premier élève). Un parcours n'en passe que ce qu'il traverse — un
 * individuel n'a ni retard ni absence.
 *
 * Contrôlé : l'étape, chaque choix, l'envoi et les boîtes arrivent en props (§ « Contrôlé »). Les
 * règles qui décident l'enchaînement (sauter le rattrapage, les 30 min avant la séance) sont celles
 * du view-model ; la feuille ne fait que montrer l'étape qu'on lui donne.
 */
import React from "react"

import { Button } from "../../atoms/Buttons/Button"
import { Stack } from "../../atoms/containers/Stack"
import { FlowOutcome } from "../../molecules/FlowOutcome"
import { TutorAbsenceConfirmStep, type TutorAbsenceConfirmStepProps } from "../../organisms/TutorAbsenceConfirmStep"
import {
    canSendAbsenceMessage,
    TutorAbsenceMessageStep,
    type TutorAbsenceMessageStepProps,
} from "../../organisms/TutorAbsenceMessageStep"
import { TutorDelayConfirmStep } from "../../organisms/TutorDelayConfirmStep"
import { TutorDelayMinutesStep, type TutorDelayMinutesStepProps } from "../../organisms/TutorDelayMinutesStep"
import { TutorMakeupStep, type TutorMakeupStepProps } from "../../organisms/TutorMakeupStep"
import { hasNewDate, TutorNewDateStep, type TutorNewDateStepProps } from "../../organisms/TutorNewDateStep"
import {
    canConfirmReschedule,
    TutorRescheduleReasonStep,
    type TutorRescheduleReasonStepProps,
} from "../../organisms/TutorRescheduleReasonStep"
import { TutorSituationStep, type TutorSituationStepProps } from "../../organisms/TutorSituationStep"
import { formatDayTime } from "../../../ports/primary/ui/formatters/dateFormatter"
import { type UITutorWeekSession } from "../../../ports/primary/ui/types/tutorLives"
import { ParentFlowModal } from "../ParentFlowModal"
import { TutorManageSessionDialog, type TutorManageSessionDialogKind } from "../TutorManageSessionDialog"

export type TutorManageSessionStep =
    | "situation"
    | "delayMinutes"
    | "delayConfirm"
    | "absenceConfirm"
    | "makeup"
    | "absenceMessage"
    | "newDate"
    | "rescheduleReason"
    | "done"

/** Ce que le parcours donne lui-même à ses étapes : elles ne le reçoivent pas une seconde fois. */
type Shared = "session" | "slotLabel" | "isIndividual" | "firstStudentName" | "testID"

export type TutorManageSessionFlowProps = {
    visible: boolean
    onClose: () => void
    step: TutorManageSessionStep
    onBack?: () => void
    session: UITutorWeekSession
    /** Un individuel ne se signale pas : il se déplace. */
    isIndividual: boolean
    /** « le mardi à 17h00 » — le créneau régulier (`readableDayTime`). */
    slotLabel: string
    /** « Jules » — le premier élève, dans l'exemple des consignes et dans l'accord de l'élève. */
    firstStudentName: string

    /** « Décris ta situation » / « Que souhaites-tu faire ? ». */
    situation: Omit<TutorSituationStepProps, Shared>
    /** Le retard : combien, puis la confirmation. */
    delay?: Omit<TutorDelayMinutesStepProps, Shared>
    /** « Confirme ton absence ». */
    absence?: Omit<TutorAbsenceConfirmStepProps, Shared>
    makeup?: Omit<TutorMakeupStepProps, Shared>
    /** Le message aux familles et les consignes du remplaçant. */
    message?: Omit<TutorAbsenceMessageStepProps, Shared>
    /** La nouvelle date d'un individuel — l'étape de la raison la rappelle. */
    newDate?: Omit<TutorNewDateStepProps, Shared>
    reschedule?: Omit<TutorRescheduleReasonStepProps, Shared | "moveKind" | "newDate" | "newSlot">

    onContinue: () => void
    onSubmit: () => void
    submitting?: boolean
    errorMessage?: string

    dialog?: TutorManageSessionDialogKind | null
    onDialogConfirm?: () => void
    onDialogClose?: () => void
    testID?: string
}

const NO_DATE: Omit<TutorNewDateStepProps, Shared> = {
    moveKind: "one_occurrence",
    newDate: "",
    newSlot: { weekday: null, time: null },
}

/** « Séance d'aujourd'hui à 17h00 · Cours partagé · Jules et Léa » — le rappel en tête de chaque étape. */
function sessionReminder(session: UITutorWeekSession): string {
    return [`Séance ${formatDayTime(session.date, true, "complement")}`, session.kindLabel, session.studentsLabel]
        .filter(Boolean)
        .join(" · ")
}

/** Où en est le parcours, et sur combien d'étapes : la barre de l'en-tête. */
function progress(step: TutorManageSessionStep, isIndividual: boolean): { index: number; total: number } {
    if (isIndividual) {
        const order: TutorManageSessionStep[] = ["situation", "newDate", "rescheduleReason", "done"]
        return { index: Math.min(order.indexOf(step) + 1, 3), total: 3 }
    }
    if (step === "delayMinutes" || step === "delayConfirm") return { index: step === "delayMinutes" ? 2 : 3, total: 3 }
    const order: TutorManageSessionStep[] = ["situation", "absenceConfirm", "makeup", "absenceMessage"]
    const index = order.indexOf(step)
    return { index: index < 0 ? 4 : index + 1, total: 4 }
}

export function TutorManageSessionFlow(props: TutorManageSessionFlowProps) {
    const {
        visible,
        onClose,
        step,
        onBack,
        session,
        isIndividual,
        slotLabel,
        firstStudentName,
        situation,
        delay,
        absence,
        makeup,
        message,
        reschedule,
        onContinue,
        onSubmit,
        submitting = false,
        errorMessage,
        dialog,
        onDialogConfirm,
        onDialogClose,
        testID,
    } = props
    const newDate = props.newDate ?? NO_DATE
    const lateMinutes = delay?.minutes ?? null

    const cancel = <Button label="Annuler" variant="outline" onPress={onClose} />
    const footer = (label: string, onPress: () => void, enabled: boolean, busy = false) => (
        <Stack gap="2">
            <Button label={label} onPress={onPress} loading={busy} disabled={!enabled || busy} fullWidth />
            {cancel}
        </Stack>
    )

    const content = ((): { title: string; body: React.ReactNode; footer: React.ReactNode } => {
        switch (step) {
            case "situation":
                return {
                    title: isIndividual ? "Que souhaites-tu faire ?" : "Décris ta situation",
                    footer: cancel,
                    body: <TutorSituationStep {...situation} isIndividual={isIndividual} slotLabel={slotLabel} />,
                }
            case "delayMinutes":
                return {
                    title: "Je vais être en retard de",
                    footer: footer("Continuer", onContinue, lateMinutes != null),
                    body: delay != null && <TutorDelayMinutesStep {...delay} />,
                }
            case "delayConfirm":
                return {
                    title: "Signaler un retard",
                    footer: footer("Confirmer", onSubmit, true, submitting),
                    body: <TutorDelayConfirmStep minutes={lateMinutes ?? 0} />,
                }
            case "absenceConfirm":
                return {
                    title: "Confirme ton absence",
                    footer: footer("Continuer", onContinue, absence?.confirmed === true),
                    body: absence != null && <TutorAbsenceConfirmStep {...absence} session={session} />,
                }
            case "makeup":
                return {
                    title: "Proposer un rattrapage à tes élèves ?",
                    footer: footer("Continuer", onContinue, true),
                    body: makeup != null && <TutorMakeupStep {...makeup} />,
                }
            case "absenceMessage":
                return {
                    title: "Pour confirmer la demande d'absence, indique la raison",
                    footer: footer(
                        "Envoyer ma demande d'absence",
                        onSubmit,
                        message != null && canSendAbsenceMessage(message),
                        submitting
                    ),
                    body: message != null && (
                        <TutorAbsenceMessageStep {...message} firstStudentName={firstStudentName} />
                    ),
                }
            case "newDate":
                return {
                    title:
                        newDate.moveKind === "one_occurrence"
                            ? "Déplacer cette séance au :"
                            : "Nouveau créneau régulier",
                    footer: footer(
                        "Continuer",
                        onContinue,
                        hasNewDate(newDate.moveKind, newDate.newDate, newDate.newSlot)
                    ),
                    body: <TutorNewDateStep {...newDate} />,
                }
            case "rescheduleReason":
                return {
                    title: "Pour confirmer le déplacement, indique la raison",
                    footer: footer(
                        "Confirmer le déplacement",
                        onSubmit,
                        reschedule != null && canConfirmReschedule(reschedule),
                        submitting
                    ),
                    body: reschedule != null && (
                        <TutorRescheduleReasonStep
                            {...reschedule}
                            session={session}
                            slotLabel={slotLabel}
                            moveKind={newDate.moveKind}
                            newDate={newDate.newDate}
                            newSlot={newDate.newSlot}
                            firstStudentName={firstStudentName}
                        />
                    ),
                }
            case "done":
            default:
                return {
                    title: "Effectué",
                    footer: <Button label="Fermer" variant="outline" onPress={onClose} />,
                    body:
                        lateMinutes != null && !isIndividual ? (
                            <FlowOutcome
                                iconName="check"
                                tone="success"
                                message="Merci de vérifier que chaque élève a des activités pertinentes à faire en attendant ton arrivée."
                                detail="Il faudra compenser ce retard en fin de séance !"
                            />
                        ) : (
                            <FlowOutcome iconName="check" tone="success" message="Opération réussie !" />
                        ),
                }
        }
    })()

    const { index, total } = progress(step, isIndividual)

    return (
        <>
            <ParentFlowModal
                visible={visible}
                onClose={onClose}
                title={isIndividual ? "Déplacer une séance" : "Gérer une séance"}
                stepTitle={content.title}
                step={index}
                totalSteps={total}
                onBack={step === "situation" || step === "done" ? undefined : onBack}
                context={step === "done" ? undefined : sessionReminder(session)}
                errorMessage={errorMessage}
                footer={content.footer}
                testID={testID}
            >
                {content.body}
            </ParentFlowModal>
            <TutorManageSessionDialog
                dialog={dialog ?? null}
                impact={absence?.impact ?? null}
                firstStudentName={firstStudentName}
                onConfirm={() => onDialogConfirm?.()}
                onClose={() => onDialogClose?.()}
            />
        </>
    )
}
